package com.islamicmedia.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * User "Display style" preference for analysis results — driven by two sliders:
 *  - [textScale]: a continuous text-size multiplier (1.0 = current/default).
 *  - [colorDarkness]: how dark the body prose is, from 0 (current gray) to 1 (black).
 * Persisted locally via SettingsStorage and applied to the analysis result screen (+ the preview).
 * Each platform maps these onto its own styling (font scale + interpolated text color).
 */
@Serializable
data class DisplaySettings(
    val textScale: Float = 1.0f,
    val colorDarkness: Float = 0.0f,
    /**
     * How densely "Your Analyses" is laid out: 1, 2 or 3 across.
     *
     * Stored as a PLAIN INT rather than an enum on purpose. This whole object is persisted as one
     * JSON blob and decoded inside a try/catch that falls back to [DEFAULT] on any failure — so an
     * enum value written by a newer build would not just lose this one setting on an older client,
     * it would silently reset the user's text size and darkness along with it. An int cannot fail
     * to decode, and [analysesLayout] clamps whatever it finds.
     *
     * 1 is the LIST layout, not a one-wide grid: see [AnalysesLayout.LIST].
     */
    val analysesColumns: Int = 2,
) {
    /** Clamped multiplier applied to ALL analysis text. 1.0 = unchanged from before this setting. */
    val fontScale: Float get() = textScale.coerceIn(MIN_SCALE, MAX_SCALE)

    /** Clamped 0 (gray) … 1 (black) for the body-prose color interpolation. */
    val darkness: Float get() = colorDarkness.coerceIn(0f, 1f)

    /** [analysesColumns] as the thing the UI actually switches on. */
    val analysesLayout: AnalysesLayout get() = when (analysesColumns) {
        1 -> AnalysesLayout.LIST
        3 -> AnalysesLayout.GRID_3
        else -> AnalysesLayout.GRID_2
    }

    companion object {
        /** Slider bounds for [textScale]. */
        const val MIN_SCALE = 0.85f
        const val MAX_SCALE = 1.40f
        val DEFAULT = DisplaySettings()
    }
}

/**
 * The three ways "Your Analyses" can be laid out, in the order they're offered.
 *
 * The cover art is the reason this setting exists: at two across the artwork is large enough to
 * dominate the screen, and people with a long catalogue asked to see more titles at once. [LIST]
 * answers that by inverting the emphasis — a thumbnail beside the text rather than text beneath a
 * poster — while [GRID_3] just shrinks the same card.
 */
enum class AnalysesLayout(val columns: Int, val label: String) {
    /** One per row: small cover thumbnail, then title and year, then the item menu. */
    LIST(1, "List"),

    /** Two across. The default, and what every user had before this setting existed. */
    GRID_2(2, "2 columns"),

    /** Three across — same card, smaller. */
    GRID_3(3, "3 columns"),
}
